/* eslint-disable */
import { BehaviorSubject, Subject } from "rxjs";
import { isAuth, saveToken, removeToken } from "../utils/auth";
import { browserHistory } from "../../../../routes/history";
import {
  EMAIL_EXISTS_ERROR,
  SERVER_INTERNAL_ERROR,
  BAD_REQUEST_ERROR,
  REQUEST_FAILED_DEPENDENCY,
  PASSWORD_EXPIRED_ERROR,
} from "../constants/constants";
import { get, OK_RESPONSE, post } from "../../../http";

export interface AuthServiceConfig {
  baseUrl?: string;
  liveTime?: number; // TODO: Подключить
}

export interface User {
  // TODO: Вынести в папку моделей
  name: string;
  surname: string;
  departmentId: number;
  email: string;
  password?: string;
}

export enum CheckLifeTimeTypeLinks {
  None = "None",
  ResetPassword = "ResetPassword",
  PasswordAdd = "PasswordAdd",
  EmailConfirm = "EmailConfirm",
  ExpiredPassword = "expiredPasswordCheckLink"
}

export enum AuthError {
  None,
  Network,
  Server,
  InvalidCredentials,
  NotVerified,
  EmailExists,
  BadRequest,
  FailedDependency,
  CannotReusePreviousPassword,
}

export enum RequestStatus {
  None,
  Pending,
  Success,
  Fail,
}

export class AuthService {
  private _baseUrl: string;
  private _isAuth$ = new BehaviorSubject<boolean>(isAuth());
  private _error$ = new Subject<AuthError>();
  private _status$ = new BehaviorSubject<RequestStatus>(RequestStatus.None);
  public get error$() {
    return this._error$.asObservable();
  }

  public get status$() {
    return this._status$.asObservable();
  }

  public get isAuth$() {
    return this._isAuth$.asObservable();
  }

  public get isAuth() {
    return this._isAuth$.value;
  }

  constructor(config?: AuthServiceConfig) {
    this._baseUrl = (config?.baseUrl || "/backend/api") + "/authorization";
    setInterval(() => {
      if (this.isAuth !== isAuth()) {
        this._isAuth$.next(isAuth());
      }
    }, 2000);
  }

  async login(email: string, password: string) {
    if (!this.isAuth) {
      try {
        this._status$.next(RequestStatus.Pending);
        await fetch(`${this._baseUrl}/token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            password,
          }),
        }).then((response) => {
          this.handleAuthorizationRequest(response);
        });
      } catch (err) {
        // TODO: Добавить логику обработки ошибок
        throw err;
      }
    } else {
      removeToken();
      this._isAuth$.next(false);
      await this.login(email, password);
    }
  }

  async refresh() {
    if (this.isAuth) {
      try {
        const response = await post(`${this._baseUrl}/refresh`);
        if (response.status === OK_RESPONSE) {
          const token = await response.text();
          saveToken(token);
        }
      } catch (err) {
        console.error("No connection to server");
      }
    }
  }

  async registration(user: User) {
    // TODO: Добавить логику если это делает админ. мб и не тут будет
    try {
      this._status$.next(RequestStatus.Pending);
      await fetch(`${this._baseUrl}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      }).then((response) => {
        this.handleRegistrationRequest(response);
      });
    } catch (err) {
      // TODO: Добавить логику обработки ошибок
      throw err;
    }
  }

  handleAuthorizationRequest = async (response: Response) => {
    if (response.ok) {
      const token = await response.text();
      saveToken(token);
      if (token) {
        this._isAuth$.next(true);
        this._status$.next(RequestStatus.Success);
        browserHistory.push("/");
      } else {
        this._status$.next(RequestStatus.Fail);
      }
    } else {
      this._status$.next(RequestStatus.Fail);
      if (response.status === PASSWORD_EXPIRED_ERROR) {
        localStorage.setItem("isPasswordExpired", "true");
        const token = (await response.json()).token;
        saveToken(token);
        this._isAuth$.next(true);
        this._status$.next(RequestStatus.Success);
      }
      if (response.status === SERVER_INTERNAL_ERROR) {
        this._error$.next(AuthError.Server);
      }
      if (response.status === BAD_REQUEST_ERROR) this._error$.next(AuthError.BadRequest);
    }
  };

  handleRegistrationRequest = (response: Response) => {
    if (response.ok) {
      this._status$.next(RequestStatus.Success);
      this._error$.next(AuthError.None);
      browserHistory.push("/login/register-complete");
    } else {
      this._status$.next(RequestStatus.Fail);
      if (response.status === SERVER_INTERNAL_ERROR) {
        this._error$.next(AuthError.Server);
      }
      if (response.status === EMAIL_EXISTS_ERROR) {
        this._error$.next(AuthError.EmailExists);
      }
      if (response.status === REQUEST_FAILED_DEPENDENCY) {
        this._error$.next(AuthError.FailedDependency);
      }
    }
  };

  logout = () => {
    removeToken();
    localStorage.removeItem("isPasswordExpired");
    this._isAuth$.next(false);
    browserHistory.push("/login")
  };

  async passwordResetRequest(email: string, isRepeatedLetter?: boolean) {
    try {
      this._status$.next(RequestStatus.Pending);
      await fetch(`${this._baseUrl}/passwordResetSendLink`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      }).then(async (response) => {
        if (response.ok) {
          this._status$.next(RequestStatus.Success);
          this._error$.next(AuthError.None);
          if (!isRepeatedLetter) {
            browserHistory.push("/login/password-reset-request");
          }
        } else {
          const message = await response.text();
          if (response.status === 400 && message === "Email not registered") {
            this._status$.next(RequestStatus.Fail);
            this._error$.next(AuthError.EmailExists);
          } else {
            this._status$.next(RequestStatus.Fail);
            this._error$.next(AuthError.Server);
          }
        }
      });
    } catch (err) {
      // console.error("request fail");
      // console.error(err);
      throw err;
    }
  }

  async checkLifeTimeLink(userId: number, checkLifeTimeTypeLinks: CheckLifeTimeTypeLinks) {
    try {
      this._status$.next(RequestStatus.Pending);

      await get(`${this._baseUrl}/checkLifeTimeLink`, { userId }).then(async (response) => {
        if (response.ok) {
          this._status$.next(RequestStatus.Success);
          const result = response.text();
          await result.then((value) => {
            if (value && value !== "false") {
              browserHistory.push(`/login/request-new-link?email=${value}&type=${checkLifeTimeTypeLinks}`);
            }
          });
        } else {
          this._status$.next(RequestStatus.Success);
        }
      });
    } catch (err) {
      this._status$.next(RequestStatus.Fail);
      throw err;
    }
  }

  async passwordReset(userId: number, code: string, newPassword: string) {
    try {
      this._status$.next(RequestStatus.Pending);
      await fetch(`${this._baseUrl}/passwordReset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, code, newPassword }),
      }).then(async (response) => {
        if (response.ok) {
          this._status$.next(RequestStatus.Success);
          this._error$.next(AuthError.None);
          browserHistory.push("/login");
        } else {
          this._status$.next(RequestStatus.Fail);
          const message = await response.json();
          if (message && message[0].code === "CannotReusePreviousPassword") {
            this._error$.next(AuthError.CannotReusePreviousPassword);
          }else {
            this._error$.next(AuthError.Server);
          }
        }
      });
    } catch (err) {
      throw err;
    }
  }

  async passwordAdd(userId: number, code: string, newPassword: string) {
    try {
      this._status$.next(RequestStatus.Pending);

      await fetch(`${this._baseUrl}/passwordAdd`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, code, newPassword }),
      }).then(async (response) => {
        if (response.ok) {
          this._status$.next(RequestStatus.Success);
          this._error$.next(AuthError.None);

          const email = await response.text();
          email && (await this.login(email, newPassword));
          browserHistory.push("/");
        } else {
          this._status$.next(RequestStatus.Fail);
          this._error$.next(AuthError.Server);
        }
      });
    } catch (err) {
      throw err;
    }
  }

  async repeatSendConfirmationEmail(email: string) {
    try {
      this._status$.next(RequestStatus.Pending);

      await post(`${this._baseUrl}/repeatSendConfirmationEmail`, { email }).then(async (response) => {
        if (response.ok) {
          this._status$.next(RequestStatus.Success);
        } else {
          this._status$.next(RequestStatus.Fail);
        }
      });
    } catch (err) {
      this._status$.next(RequestStatus.Fail);
      throw err;
    }
  }

  async repeatSendPassWordExpiredEmail(email: string) {
    try {
      this._status$.next(RequestStatus.Pending);

      await post(`${this._baseUrl}/repeatSendPassWordExpiredEmail`, { email }).then(async (response) => {
        if (response.ok) {
          this._status$.next(RequestStatus.Success);
        } else {
          this._status$.next(RequestStatus.Fail);
        }
      });
    } catch (err) {
      this._status$.next(RequestStatus.Fail);
      throw err;
    }
  }
  async repeatSendInviteEmail(email: string) {
    try {
      this._status$.next(RequestStatus.Pending);

      await post(`${this._baseUrl}/repeatSendInviteEmail`, { email }).then(async (response) => {
        if (response.ok) {
          this._status$.next(RequestStatus.Success);
        } else {
          this._status$.next(RequestStatus.Fail);
        }
      });
    } catch (err) {
      this._status$.next(RequestStatus.Fail);
      throw err;
    }
  }
}
